import { useState, useCallback, ReactNode } from 'react';

export interface BreadcrumbItem {
  label: string | ReactNode;
  path?: string;
}

export interface UseBreadcrumbReturn {
  items: BreadcrumbItem[];
  setItems: (items: BreadcrumbItem[]) => void;
  addItem: (item: BreadcrumbItem) => void;
  removeLastItem: () => void;
  updateLastItem: (newLabel: string) => void;
  updateItemAtIndex: (index: number, newItem: Partial<BreadcrumbItem>) => void;
}

/**
 * Custom hook for managing breadcrumb navigation state
 * @param initialItems - Initial breadcrumb items
 * @returns Object with breadcrumb state and helper functions
 */
export const useBreadcrumb = (initialItems: BreadcrumbItem[] = []): UseBreadcrumbReturn => {
  const [items, setItems] = useState<BreadcrumbItem[]>(initialItems);

  const addItem = useCallback((item: BreadcrumbItem) => {
    setItems((prevItems) => [...prevItems, item]);
  }, []);

  const removeLastItem = useCallback(() => {
    setItems((prevItems) => prevItems.slice(0, -1));
  }, []);

  const updateLastItem = useCallback((newLabel: string) => {
    setItems((prevItems) => {
      if (prevItems.length === 0) return prevItems;

      const newItems = [...prevItems];
      newItems[newItems.length - 1] = {
        ...newItems[newItems.length - 1],
        label: newLabel,
      };
      return newItems;
    });
  }, []);

  const updateItemAtIndex = useCallback((index: number, newItem: Partial<BreadcrumbItem>) => {
    setItems((prevItems) => {
      if (index < 0 || index >= prevItems.length) return prevItems;

      const newItems = [...prevItems];
      newItems[index] = {
        ...newItems[index],
        ...newItem,
      };
      return newItems;
    });
  }, []);

  return {
    items,
    setItems,
    addItem,
    removeLastItem,
    updateLastItem,
    updateItemAtIndex,
  };
};
